<template>
  <page-template :is-loading="isLoading" :data-exists="selectedContentFilterProfileData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('contentFilterProfile.configPage.newPageHeadline')
          : $t('contentFilterProfile.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Content Filter Profiles List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedContentFilterProfile && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name] as [string, string])"
          :disabled="isLoading"
          v-model:value="selectedContentFilterProfile"
          @update:value="switchContentFilterProfile($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('contentFilterProfile.configPage.switchTooltip')"
          classes="switch-content-filter-profile switch-page-entry"
          data-qa="switch-content-filter-profile"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedContentFilterProfile && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateContentFilterProfile()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadContentFilterProfile()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteContentFilterProfileModal()"
          class="delete-button"
          :disabled="
            isLoading ||
            selectedContentFilterProfileProtected ||
            selectedContentFilterProfileReferenced
          "
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isContentFilterProfileValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('contentFilterProfile.configPage.name')"
              :placeholder="$t('contentFilterProfile.configPage.name')"
              v-model:inputValue="selectedContentFilterProfileData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('contentFilterProfile.configPage.description')"
              v-model:inputValue="selectedContentFilterProfileData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="masking-seed"
              classes="masking-seed"
              data-qa="masking-seed-input"
              :label="$t('contentFilterProfile.configPage.maskingSeed')"
              :placeholder="$t('contentFilterProfile.configPage.maskingSeed')"
              :type="maskingSeedHidden ? 'password' : 'text'"
              :readonly="maskingSeedHidden"
              :disabled="readOnly"
              @focus="maskingSeedHidden = false"
              @blur="maskingSeedHidden = true"
              v-model:inputValue="selectedContentFilterProfileData['masking_seed']"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="actions.length"
              :items="actions.map((item) => [item.id, item.name] as [string, string])"
              v-model:value="selectedContentFilterProfileData.action"
              :label="$t('contentFilterProfile.configPage.action')"
              data-qa="action-dropdown"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <tags-selection
              :value="selectedContentFilterProfileData.tags"
              @update:value="selectedContentFilterProfileData.tags = $event as string[]"
              :label="$t('contentFilterProfile.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedContentFilterProfileData['ignore_alphanum']"
              :label="$t('contentFilterProfile.configPage.ignoreAlphanumericInput')"
              class="ignore-alphanumeric-input"
              data-qa="ignore-alphanumeric-toggle"
              :disabled="readOnly"
            />
            <span
              v-b-tooltip="
                $t('contentFilterProfile.configPage.ignoreAlphanumericInputAdditionalInfoTooltip')
              "
            >
              <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
            </span>
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedContentFilterProfileData['ignore_body']"
              :label="$t('contentFilterProfile.configPage.ignoreBody')"
              data-qa="ignore-body-toggle"
              :disabled="readOnly"
            />
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('contentFilterProfile.configPage.restrictContentType') }}
              <span
                v-b-tooltip="
                  $t('contentFilterProfile.configPage.restrictContentTypeAdditionalInfoTooltip')
                "
              >
                <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
              </span>
            </label>
            <div class="content-type-options-wrapper">
              <div v-for="contentTypeOption in contentTypeOptions" :key="contentTypeOption.value">
                <rbz-form-checkbox
                  :id="`${contentTypeOption.value}-input`"
                  :classes="`content-type-${contentTypeOption.value}-checkbox`"
                  :data-qa="`content-type-${contentTypeOption.value}-checkbox`"
                  :label="contentTypeOption.displayName"
                  :checked="getContentTypeStatus(contentTypeOption.value)"
                  @update:checkedValue="updateContentType(contentTypeOption.value, $event)"
                  :disabled="readOnly"
                />
                <div v-if="contentTypeOption.value === 'json' && getContentTypeStatus('json')">
                  <rbz-form-input
                    name="graphql-path"
                    class="graphql-path-input"
                    data-qa="graphql-path-input"
                    :label="
                      $t('contentFilterProfile.configPage.restrictContentTypeJsonGraphqlPath')
                    "
                    :placeholder="
                      $t('contentFilterProfile.configPage.restrictContentTypeJsonGraphqlPath')
                    "
                    v-model:inputValue="selectedContentFilterProfileData['graphql_path']"
                    :disabled="readOnly"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('contentFilterProfile.decoding') }}
            </label>
            <div class="decoding-options-wrapper" v-if="selectedContentFilterProfileData.decoding">
              <div v-for="decodingOption in decodingOptions" :key="decodingOption.value">
                <rbz-form-checkbox
                  :id="`decoding-${decodingOption.value}-checkbox`"
                  :label="decodingOption.displayName"
                  :data-qa="`decoding-${decodingOption.value}-checkbox`"
                  :checked="
                    selectedContentFilterProfileData.decoding[
                      decodingOption.value as ContentFilterProfileDecodings
                    ]
                  "
                  @update:checkedValue="
                    selectedContentFilterProfileData.decoding[
                      decodingOption.value as ContentFilterProfileDecodings
                    ] = $event
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <rbz-card-collapsible data-qa="tags-card">
        <template #title>
          <div>
            <span class="inline-collapsed-header">
              <span class="rbz-label rbz-label-inline width-60px">
                {{ $t('contentFilterProfile.configPage.tagsValue.ignore') }}:
              </span>
              <span class="is-fullwidth ellipsis">
                {{
                  $t('contentFilterProfile.configPage.tagsValue.headerValue', {
                    value: selectedContentFilterProfileData.ignore?.length || 0,
                  })
                }}
              </span>
            </span>
            <span class="inline-collapsed-header">
              <span class="rbz-label rbz-label-inline width-60px">
                {{ $t('contentFilterProfile.configPage.tagsValue.active') }}:
              </span>
              <span class="is-fullwidth ellipsis">
                {{
                  $t('contentFilterProfile.configPage.tagsValue.headerValue', {
                    value: selectedContentFilterProfileData.active?.length || 0,
                  })
                }}
              </span>
            </span>
            <span class="inline-collapsed-header">
              <span class="rbz-label rbz-label-inline width-60px">
                {{ $t('contentFilterProfile.configPage.tagsValue.report') }}:
              </span>
              <span class="is-fullwidth ellipsis">
                {{
                  $t('contentFilterProfile.configPage.tagsValue.headerValue', {
                    value: selectedContentFilterProfileData.report?.length || 0,
                  })
                }}
              </span>
            </span>
          </div>
        </template>
        <template #content>
          <div class="rbz-columns">
            <tags-selection-table
              :columns="tagsSelectionTableColumns"
              @update:tags="
                selectedContentFilterProfileData[$event.fieldName as ContentFilterProfileTagLists] =
                  $event.tags
              "
              :disabled="readOnly"
            />
          </div>
        </template>
      </rbz-card-collapsible>
      <div class="left rbz-text-h3">
        {{ $t('contentFilterProfile.configPage.constraintsTableTitle') }}
      </div>
      <div class="rbz-card white constraints-card">
        <tabs-wrapper :tabs="constraintsSections">
          <template v-for="section in constraintsSections" :key="section.title" #[section.title]>
            <div class="rbz-columns" v-if="section.sectionType !== 'allsections'">
              <div class="rbz-column is-4">
                <div class="rbz-field">
                  <rbz-form-input
                    name="max-length"
                    :classes="`max-${section.sectionType}-length-input`"
                    :data-qa="`max-${section.sectionType}-length-input`"
                    type="number"
                    number-type="whole"
                    :label="$t('contentFilterProfile.configPage.constraints.maxLength')"
                    :placeholder="
                      $t('contentFilterProfile.configPage.constraints.maxLengthFor', {
                        section: section.title.toLowerCase(),
                      })
                    "
                    v-model:inputValue="
                      selectedContentFilterProfileData[section.sectionType]['max_length']
                    "
                    :disabled="readOnly"
                  />
                </div>
                <div class="rbz-field">
                  <rbz-form-toggle
                    v-model:buttonValue="
                      selectedContentFilterProfileData[section.sectionType]['enable_max_length']
                    "
                    :label="$t('contentFilterProfile.configPage.constraints.maxLengthActive')"
                    data-qa="max-length-toggle"
                    :disabled="readOnly"
                  />
                </div>
                <div class="rbz-field">
                  <rbz-form-input
                    name="max-count"
                    :classes="`max-${section.sectionType}-count-input`"
                    :data-qa="`max-${section.sectionType}-count-input`"
                    type="number"
                    number-type="whole"
                    :label="$t('contentFilterProfile.configPage.constraints.maxCount')"
                    :placeholder="
                      $t('contentFilterProfile.configPage.constraints.maxCountFor', {
                        section: section.title.toLowerCase(),
                      })
                    "
                    v-model:inputValue="
                      selectedContentFilterProfileData[section.sectionType]['max_count']
                    "
                    :disabled="readOnly"
                  />
                </div>
                <div class="rbz-field">
                  <rbz-form-toggle
                    v-model:buttonValue="
                      selectedContentFilterProfileData[section.sectionType]['enable_max_count']
                    "
                    :label="$t('contentFilterProfile.configPage.constraints.maxCountActive')"
                    data-qa="max-count-toggle"
                    :disabled="readOnly"
                  />
                </div>
              </div>
            </div>
            <rbz-table
              :columns="constraintsColumns"
              :table-data="getConstraintsData(section.sectionType)"
              :row-buttons="constraintsRowButtons"
              :show-new-button="!readOnly"
              :show-buttons-column="!readOnly"
              :show-new-as-row="true"
              :show-download-button="false"
              :show-filter-button="true"
              :show-filter-row="true"
              :show-new-default-data="defaultNewEntryConstraints"
              ref="constraintsTable"
              rows-per-page="20"
              data-qa="constraints-table"
              @add-inline-data="
                addConstraint(section.sectionType, $event as ContentFilterEntryMatch)
              "
              @edit-inline-data="
                editConstraint(section.sectionType, $event as ContentFilterEntryMatch)
              "
              @delete-row-button-clicked="
                deleteConstraint(section.sectionType, $event as ContentFilterEntryMatch)
              "
            >
              <template #newRowOverride="newRowProps">
                <td>
                  <div class="parameter-field-wrapper">
                    <rbz-dropdown
                      v-if="newRowProps.mode === 'new'"
                      :items="[
                        ['names', $t('contentFilterProfile.configPage.constraints.names')],
                        ['regex', $t('contentFilterProfile.configPage.constraints.regex')],
                      ]"
                      data-qa="new-entry-type-dropdown"
                      classes="new-entry-type-dropdown width-120px"
                      v-model:value="newRowProps.data.type"
                    />
                    <rbz-form-input
                      name="key"
                      :icon="newRowProps.data.type === 'names' ? 'IconAlphabet' : 'IconCode'"
                      icon-location="left"
                      class="is-fullwidth"
                      classes="new-entry-key-input"
                      data-qa="new-entry-key-input"
                      :placeholder="$t('contentFilterProfile.configPage.constraints.key')"
                      v-model:inputValue="newRowProps.data.key"
                      :is-invalid="
                        !!newConstraintKeyError(
                          section.sectionType,
                          newRowProps.data as ContentFilterEntryMatch
                        )
                      "
                      :error-message-text="
                        newConstraintKeyError(
                          section.sectionType,
                          newRowProps.data as ContentFilterEntryMatch
                        )
                      "
                    />
                  </div>
                </td>
                <td>
                  <rbz-form-input
                    name="matching-value"
                    icon="IconCode"
                    icon-location="left"
                    classes="new-entry-value-input"
                    data-qa="new-entry-value-input"
                    :placeholder="$t('contentFilterProfile.configPage.constraints.matchingValue')"
                    v-model:inputValue="newRowProps.data.reg"
                    :is-invalid="
                      !!newConstraintValueError(newRowProps.data as ContentFilterEntryMatch)
                    "
                    :error-message-text="
                      newConstraintValueError(newRowProps.data as ContentFilterEntryMatch)
                    "
                  />
                </td>
                <td>
                  <rbz-form-toggle
                    v-model:buttonValue="newRowProps.data.restrict"
                    classes="new-entry-restrict-checkbox"
                    data-qa="new-entry-restrict-checkbox"
                    :id="`restrict-${newRowProps.data.id}`"
                    @update:buttonValue="newRowProps.data.restrict = $event"
                  />
                </td>
                <td>
                  <rbz-form-toggle
                    v-model:buttonValue="newRowProps.data.mask"
                    classes="new-entry-mask-checkbox"
                    data-qa="mask-toggle"
                    :id="`mask-${newRowProps.data.id}`"
                    @update:buttonValue="newRowProps.data.mask = $event"
                  />
                </td>
                <td>
                  <tags-selection
                    v-model:value="newRowProps.data.ignore_cf_rule_tags"
                    list-type="content-filter-rules"
                    @update:value="
                      updateEntryIgnoreContentFilterRuleTags(
                        newRowProps.data as ContentFilterEntryMatch,
                        $event as string[]
                      )
                    "
                  />
                </td>
              </template>
              <template #keyCell="cellProps">
                <icon-alphabet v-if="cellProps.row.type === 'names'" color="shuttle-gray" />
                <icon-code v-else color="shuttle-gray" />
                <span class="ellipsis">{{ cellProps.row.key }}</span>
              </template>
              <template #ignoreContentFilterRuleTagsCell="cellProps">
                <span class="excluded-tags-cell ellipsis">
                  <template v-for="tag in cellProps.row.ignore_cf_rule_tags" :key="tag">
                    <rbz-custom-tag :value="tag" :disabled="readOnly" />
                  </template>
                </span>
              </template>
              <template #newRowOverrideConfirmationButtons="rowProps">
                <td class="row-buttons-cell">
                  <form-confirmation
                    @confirm="
                      rowProps.mode === 'new'
                        ? addConstraint(
                            section.sectionType,
                            rowProps.data as ContentFilterEntryMatch
                          )
                        : editConstraint(
                            section.sectionType,
                            rowProps.data as ContentFilterEntryMatch
                          )
                    "
                    :is-invalid="
                      !!newConstraintError(
                        section.sectionType,
                        rowProps.data as ContentFilterEntryMatch
                      )
                    "
                    @cancel="
                      rowProps.mode === 'new'
                        ? cancelAddConstraint()
                        : cancelEditConstraint(rowProps.data.id)
                    "
                  />
                </td>
              </template>
            </rbz-table>
          </template>
        </tabs-wrapper>
      </div>
      <div class="left rbz-text-h3">
        {{ $t('contentFilterProfile.configPage.exclusionTableTitle') }}
      </div>
      <div class="rbz-card white exclusions-card">
        <tabs-wrapper :tabs="exclusionSections">
          <template v-for="section in exclusionSections" :key="section.title" #[section.title]>
            <rbz-table
              :columns="exclusionColumns"
              :class="`exclusions-table ${section.sectionType}`"
              :table-data="getExclusionsData(section.sectionType)"
              :row-buttons="exclusionRowButtons"
              :show-new-as-row="true"
              :show-download-button="false"
              :show-new-default-data="defaultNewEntryExclusions"
              :show-filter-row="true"
              rows-per-page="20"
              data-qa="exclusions-table"
              @add-inline-data="
                addExclusion(section.sectionType, $event as ContentFilterEntryMatch)
              "
              @edit-inline-data="
                editExclusion(section.sectionType, $event as ContentFilterEntryMatch)
              "
              @delete-row-button-clicked="
                deleteExclusion(section.sectionType, $event as ContentFilterEntryMatch)
              "
            >
              <template #newRowOverride="newRowProps">
                <td>
                  <div class="parameter-field-wrapper">
                    <rbz-dropdown
                      v-if="newRowProps.mode === 'new'"
                      :placeholder="$t('contentFilterProfile.configPage.exclusion.mode')"
                      :items="[
                        ['text', $t('contentFilterProfile.configPage.exclusion.text')],
                        ['regex', $t('contentFilterProfile.configPage.exclusion.regex')],
                      ]"
                      data-qa="new-entry-type-dropdown"
                      classes="new-entry-type-dropdown width-120px"
                      v-model:value="newRowProps.data.type"
                    />
                    <rbz-form-input
                      name="domain"
                      :icon="newRowProps.data.type === 'regex' ? 'IconCode' : 'IconAlphabet'"
                      icon-location="left"
                      classes="new-entry-domain-input"
                      data-qa="new-entry-domain-input"
                      :placeholder="$t('contentFilterProfile.configPage.exclusion.domain')"
                      v-model:inputValue="newRowProps.data.domain"
                    />
                  </div>
                </td>
                <td>
                  <rbz-form-input
                    name="path"
                    icon="IconCode"
                    icon-location="left"
                    classes="new-entry-path-input"
                    data-qa="new-entry-path-input"
                    :placeholder="$t('contentFilterProfile.configPage.exclusion.path')"
                    v-model:inputValue="newRowProps.data.path"
                  />
                </td>
                <td>
                  <rbz-form-toggle
                    v-model:buttonValue="newRowProps.data.case_insensitive"
                    classes="new-entry-case-insensitive-checkbox"
                    data-qa="case-insensitive-toggle"
                    :id="`case-insensitive-${newRowProps.data.id}`"
                    @update:buttonValue="newRowProps.data.case_insensitive = $event"
                  />
                </td>
                <td>
                  <rbz-form-toggle
                    v-model:buttonValue="newRowProps.data.active"
                    classes="new-entry-active-checkbox"
                    data-qa="active-toggle"
                    :id="`active-${newRowProps.data.id}`"
                    @update:buttonValue="newRowProps.data.active = $event"
                  />
                </td>
                <td>
                  <tags-selection
                    v-model:value="newRowProps.data.ignore_cf_rule_tags"
                    list-type="content-filter-rules"
                    @update:value="
                      updateEntryIgnoreContentFilterRuleTags(
                        newRowProps.data as ContentFilterEntryMatch,
                        $event as string[]
                      )
                    "
                  />
                </td>
              </template>
              <template #parameterCell="cellProps">
                <icon-alphabet v-if="cellProps.row.type === 'text'" color="shuttle-gray" />
                <icon-code v-else color="shuttle-gray" />
                <span class="ellipsis">{{ cellProps.row.domain }}</span>
              </template>
              <template #ignoreContentFilterRuleTagsCell="cellProps">
                <span class="excluded-tags-cell ellipsis">
                  <template v-for="tag in cellProps.row.ignore_cf_rule_tags" :key="tag">
                    <rbz-custom-tag :value="tag" />
                  </template>
                </span>
              </template>
              <template #domainHeader>
                <div class="domain-header-wrapper">
                  <span class="rbz-label rbz-label-inline width-50px">{{
                    $t('contentFilterProfile.configPage.exclusion.domain')
                  }}</span>
                  <span
                    v-b-tooltip="$t('contentFilterProfile.configPage.exclusion.domainTooltip')"
                    class="has-tooltip"
                  >
                    <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
                  </span>
                </div>
              </template>
            </rbz-table>
          </template>
        </tabs-wrapper>
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadContentFilterProfile(selectedContentFilterProfile);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteContentFilterProfileModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteContentFilterProfile"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteContentFilterProfileModal = false"
        @closeModal="showDeleteContentFilterProfileModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedContentFilterProfileData.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  Action,
  BasicDocument,
  ContentFilterEntryMatch,
  ContentFilterProfile,
  ContentFilterProfileSection,
  ContentFilterProfileSectionType,
  DocumentName,
  NamesRegexTextType,
  TagsSelectionTableColumn,
  ContentFilterProfileTagLists,
  ContentFilterProfileDecodings,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzFormCheckbox from '@/components/RbzFormCheckbox.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import TabsWrapper from 'ui-component-library/rbz/components/TabsWrapper.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import GitHistory from '@/components/GitHistory.vue';
import TagsSelectionTable from '@/components/TagsSelectionTable.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import IconAlphabet from 'ui-component-library/assets/icons/IconAlphabet.vue';
import IconCode from 'ui-component-library/assets/icons/IconCode.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { useTagsStore } from '@/store/TagsStore';
import { usePlanetStore } from '@/store/PlanetStore';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ContentFilterProfilesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzCustomTag,
    RbzFormButton,
    RbzFormCheckbox,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    GitHistory,
    IconAlphabet,
    IconCode,
    IconInfoSmall,
    LayoutDividerLine,
    PageTemplate,
    RbzCardCollapsible,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
    TagsSelection,
    TagsSelectionTable,
    TabsWrapper,
    FormConfirmation,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const defaultNewEntryConstraints: ContentFilterEntryMatch = {
      type: 'names',
      key: '',
      reg: '',
      restrict: false,
      mask: false,
      ignore_cf_rule_tags: [],
    };
    const defaultNewEntryExclusions: ContentFilterEntryMatch = {
      type: 'text',
      key: '',
      reg: '',
      active: false,
      case_insensitive: false,
      ignore_cf_rule_tags: [],
    };
    const defaultContentFilterProfileSection: ContentFilterProfileSection = {
      names: [] as ContentFilterEntryMatch[],
      regex: [] as ContentFilterEntryMatch[],
      max_count: 0,
      enable_max_count: true,
      max_length: 0,
      enable_max_length: true,
      text: [] as ContentFilterEntryMatch[],
    };
    const defaultContentFilterProfileDecoding: ContentFilterProfile['decoding'] = {
      base64: true,
      dual: false,
      html: false,
      unicode: false,
    };
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedContentFilterProfile: null as string,
      selectedContentFilterProfileData: null as ContentFilterProfile,
      originalContentFilterProfileData: null as ContentFilterProfile,
      pageLoadingCounter: 0,
      isDeleteLoading: false,

      defaultNewEntryConstraints,
      defaultNewEntryExclusions,
      defaultContentFilterProfileSection,
      defaultContentFilterProfileDecoding,

      actions: null as Action[],
      contentTypeOptions: [
        {
          value: 'json',
          displayName: this.$t('contentFilterProfile.restrictContentTypeValue.json'),
        },
        {
          value: 'multipart_form',
          displayName: this.$t('contentFilterProfile.restrictContentTypeValue.multipartForm'),
        },
        {
          value: 'url_encoded',
          displayName: this.$t('contentFilterProfile.restrictContentTypeValue.urlEncoded'),
        },
        {
          value: 'xml',
          displayName: this.$t('contentFilterProfile.restrictContentTypeValue.xml'),
        },
        {
          value: 'graphql',
          displayName: this.$t('contentFilterProfile.restrictContentTypeValue.graphql'),
        },
      ],
      decodingOptions: [
        {
          value: 'base64',
          displayName: this.$t('contentFilterProfile.decodingValue.base64'),
        },
        {
          value: 'dual',
          displayName: this.$t('contentFilterProfile.decodingValue.url'),
        },
        {
          value: 'html',
          displayName: this.$t('contentFilterProfile.decodingValue.html'),
        },
        {
          value: 'unicode',
          displayName: this.$t('contentFilterProfile.decodingValue.unicode'),
        },
      ],
      maskingSeedHidden: true,
      constraintsSections: [
        {
          title: this.$t('contentFilterProfile.configPage.constraints.all'),
          sectionType: 'allsections',
        },
        {
          title: this.$t('contentFilterProfile.configPage.constraints.headers'),
          sectionType: 'headers',
        },
        {
          title: this.$t('contentFilterProfile.configPage.constraints.cookies'),
          sectionType: 'cookies',
        },
        {
          title: this.$t('contentFilterProfile.configPage.constraints.arguments'),
          sectionType: 'args',
        },
      ] as {
        title: string;
        sectionType: ContentFilterProfileSectionType;
      }[],
      constraintsColumns: [
        {
          id: 'key',
          title: this.$t('contentFilterProfile.configPage.constraints.parameter'),
          fieldNames: ['key'],
          displaySlotName: 'keyCell',
          tooltipFunction: (item: ContentFilterEntryMatch) => {
            return item.key;
          },
          cellContentClasses: 'key-cell',
          isSearchable: true,
        },
        {
          id: 'matching-value',
          title: this.$t('contentFilterProfile.configPage.constraints.matchingValue'),
          fieldNames: ['reg'],
          cellContentClasses: 'ellipsis matching-value-cell',
          isSearchable: true,
        },
        {
          id: 'restrict',
          title: this.$t('contentFilterProfile.configPage.constraints.restrict'),
          fieldNames: ['restrict'],
          displayFunction: (item: ContentFilterEntryMatch) => {
            return item.restrict ? this.$t('common.yes') : this.$t('common.no');
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis restrict-cell',
          isSearchable: true,
        },
        {
          id: 'mask',
          title: this.$t('contentFilterProfile.configPage.constraints.mask'),
          fieldNames: ['mask'],
          displayFunction: (item: ContentFilterEntryMatch) => {
            return item.mask ? this.$t('common.yes') : this.$t('common.no');
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis mask-cell',
          isSearchable: true,
        },
        {
          id: 'ignore_cf_rule_tags',
          title: this.$t('contentFilterProfile.configPage.constraints.ignoreContentFilterTags'),
          fieldNames: ['ignore_cf_rule_tags'],
          displaySlotName: 'ignoreContentFilterRuleTagsCell',
          tooltipFunction: () => {
            return '';
          },
          cellContentClasses: 'ellipsis ignore-cell',
          isSearchable: true,
        },
      ] as RbzTableColumnOptions[],
      constraintsRowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          editInline: true,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      exclusionSections: [
        {
          title: this.$t('contentFilterProfile.configPage.exclusion.url'),
          sectionType: 'url',
        },
      ] as { title: string; sectionType: ContentFilterProfileSectionType }[],
      exclusionColumns: [
        {
          id: 'domain',
          headerSlotName: 'domainHeader',
          displaySlotName: 'parameterCell',
          title: this.$t('contentFilterProfile.configPage.exclusion.domain'),
          fieldNames: ['domain'],
          classes: 'domain-cell',
          cellContentClasses: 'parameter-cell',
          isSearchable: true,
        },
        {
          id: 'path',
          title: this.$t('contentFilterProfile.configPage.exclusion.path'),
          fieldNames: ['path'],
          classes: 'path',
          cellContentClasses: 'ellipsis',
          isSearchable: true,
        },
        {
          id: 'case-insensitive',
          title: this.$t('contentFilterProfile.configPage.exclusion.caseInsensitive'),
          fieldNames: ['case_insensitive'],
          classes: 'case-insensitive width-100px text-center',
          displayFunction: (item: ContentFilterEntryMatch) => {
            return item.case_insensitive ? this.$t('common.yes') : this.$t('common.no');
          },
          isSearchable: true,
        },
        {
          id: 'active',
          title: this.$t('contentFilterProfile.configPage.exclusion.active'),
          fieldNames: ['active'],
          classes: 'active width-100px text-center',
          displayFunction: (item: ContentFilterEntryMatch) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          isSearchable: true,
        },
        {
          id: 'ignore-cf-rule-tags',
          title: this.$t('contentFilterProfile.configPage.exclusion.ignoreContentFilterTags'),
          fieldNames: ['ignore_cf_rule_tags'],
          displaySlotName: 'ignoreContentFilterRuleTagsCell',
          cellContentClasses: 'ignore-cf-rule-tags-cell ellipsis',
          isSearchable: true,
        },
      ] as RbzTableColumnOptions[],
      exclusionRowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          editInline: true,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      referencedIds: [] as string[],
      showDeleteContentFilterProfileModal: false,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, usePlanetStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('contentFilterProfile.plural'),
        singular: this.$t('contentFilterProfile.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/content-filter-profiles/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    selectedContentFilterProfileProtected(): boolean {
      return this.selectedContentFilterProfile?.startsWith('__');
    },

    selectedContentFilterProfileReferenced(): boolean {
      return this.referencedIds.includes(this.selectedContentFilterProfile);
    },

    deleteTooltip(): string {
      if (this.selectedContentFilterProfileProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedContentFilterProfileReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(
        this.selectedContentFilterProfileData,
        this.originalContentFilterProfileData
      );
    },

    automaticTags(): string[] {
      const name = this.tagsStore.textToTag(this.selectedContentFilterProfileData.name);
      const nameTag = `contentfiltername:${name}`;
      return [nameTag];
    },

    tagsSelectionTableColumns(): TagsSelectionTableColumn[] {
      return [
        {
          title: this.$t('contentFilterProfile.configPage.tagsValue.ignore'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'ignore',
              tags: this.selectedContentFilterProfileData['ignore'],
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-4',
        },
        {
          title: this.$t('contentFilterProfile.configPage.tagsValue.active'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'active',
              tags: this.selectedContentFilterProfileData['active'],
              listType: 'content-filter-rules',
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-4',
        },
        {
          title: this.$t('contentFilterProfile.configPage.tagsValue.report'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'report',
              tags: this.selectedContentFilterProfileData['report'],
              listType: 'content-filter-rules',
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-4',
        },
      ];
    },

    duplicateTags(): string[] {
      const allTags = _.concat(
        this.selectedContentFilterProfileData['ignore'],
        this.selectedContentFilterProfileData['active'],
        this.selectedContentFilterProfileData['report']
      );
      return _.filter(allTags, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
    },

    isContentFilterProfileValid(): boolean {
      const noDuplicates = this.duplicateTags.length === 0;
      const trimmedName = this.selectedContentFilterProfileData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedContentFilterProfileData.name &&
        (trimmedName === this.originalContentFilterProfileData.name.toLowerCase().trim() ||
          !nameExists);
      return noDuplicates && nameValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('content-filter-profiles'));
    },

    switchContentFilterProfile(contentFilterProfileId: string) {
      this.navigateTo(this.getEditPath('content-filter-profiles', contentFilterProfileId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newContentFilterProfile(): ContentFilterProfile {
      const factory = DatasetsUtils.newEntityFactory['content-filter-profiles'];
      return factory && factory();
    },

    async loadContentFilterProfile(contentFilterProfileId: string) {
      this.setPageLoadingStatus(true);
      this.selectedContentFilterProfile = contentFilterProfileId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfileId}`,
      });

      this.selectedContentFilterProfileData = response.data;
      this.originalContentFilterProfileData = _.cloneDeep(this.selectedContentFilterProfileData);
      this.setPageLoadingStatus(false);
    },

    async duplicateContentFilterProfile() {
      this.setPageLoadingStatus(true);
      const contentFilterProfileToAdd = _.cloneDeep(this.selectedContentFilterProfileData);
      contentFilterProfileToAdd.id = DatasetsUtils.generateUUID2();
      contentFilterProfileToAdd.name = Utils.generateUniqueEntityName(
        contentFilterProfileToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfileToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: contentFilterProfileToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchContentFilterProfile(contentFilterProfileToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadContentFilterProfile() {
      Utils.downloadFile('content-filter-profile', 'json', this.selectedContentFilterProfileData);
    },

    openDeleteContentFilterProfileModal() {
      this.showDeleteContentFilterProfileModal = true;
    },

    async deleteContentFilterProfile() {
      this.isDeleteLoading = true;
      const contentFilterProfileId = this.selectedContentFilterProfile;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === contentFilterProfileId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedContentFilterProfile = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfileId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.sanitizeContentFilterProfileData();
      this.selectedContentFilterProfileData.name =
        this.selectedContentFilterProfileData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/content-filter-profiles/${this.selectedContentFilterProfileData.id}`,
        data: this.selectedContentFilterProfileData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedContentFilterProfileData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalContentFilterProfileData = _.cloneDeep(
            this.selectedContentFilterProfileData
          );
        }
      }
      this.setPageLoadingStatus(false);
    },
    sanitizeContentFilterProfileData() {
      delete this.selectedContentFilterProfileData.headers.text;
      delete this.selectedContentFilterProfileData.cookies.text;
      delete this.selectedContentFilterProfileData.args.text;
      delete this.selectedContentFilterProfileData.path.text;
      delete this.selectedContentFilterProfileData.allsections.text;
      delete this.selectedContentFilterProfileData?.url?.names;
    },
    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });

      const securityPolicies = response.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['content_filter_profile']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const displayedDefaultIds = [
        'action-contentfilter-block',
        'action-waap-feed-block',
        'action-https-redirect',
      ];
      const displayedTypes: Action['type'][] = ['block'];
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name, type' } },
      });
      this.actions = _.filter(response?.data?.items, (item: Action) => {
        const hiddenDefaultId =
          item.id.startsWith('action-') && !displayedDefaultIds.includes(item.id);
        return displayedTypes.includes(item.type) && !hiddenDefaultId;
      });
      this.setPageLoadingStatus(false);
    },

    normalizeSections(section: ContentFilterProfileSectionType) {
      this.selectedContentFilterProfileData[section] = _.cloneDeep(
        this.defaultContentFilterProfileSection
      );
      if (section === 'allsections') {
        delete this.selectedContentFilterProfileData[section]['max_count'];
        delete this.selectedContentFilterProfileData[section]['max_length'];
      }
      if (section === 'url') {
        this.selectedContentFilterProfileData[section]['enable_max_count'] = false;
        this.selectedContentFilterProfileData[section]['enable_max_length'] = false;
        this.selectedContentFilterProfileData[section]['max_count'] = 42;
        this.selectedContentFilterProfileData[section]['max_length'] = 1024;
      }
    },

    normalizeContentType() {
      this.selectedContentFilterProfileData.content_type = [];
    },

    normalizeDecoding() {
      this.selectedContentFilterProfileData.decoding = _.cloneDeep(
        this.defaultContentFilterProfileDecoding
      );
    },

    getContentTypeStatus(value: string): boolean {
      return this.selectedContentFilterProfileData['content_type']?.includes(value);
    },

    updateContentType(value: string, state: boolean): void {
      if (state) {
        this.selectedContentFilterProfileData['content_type'].push(value);
      } else {
        const index = this.selectedContentFilterProfileData['content_type'].indexOf(value);
        if (index > -1) {
          this.selectedContentFilterProfileData['content_type'].splice(index, 1);
        }
      }
    },

    getConstraintsData(section: ContentFilterProfileSectionType) {
      if (!this.selectedContentFilterProfileData[section]) {
        return [];
      }
      const names = this.selectedContentFilterProfileData[section].names.map(
        (item: ContentFilterEntryMatch) => {
          item.type = 'names';
          return item;
        }
      );
      const regex = this.selectedContentFilterProfileData[section].regex.map(
        (item: ContentFilterEntryMatch) => {
          item.type = 'regex';
          return item;
        }
      );
      return _.concat(names, regex).map((item) => {
        if (!item.id) {
          item.id = DatasetsUtils.generateUUID2();
        }
        return item;
      });
    },

    getExclusionsData(section: ContentFilterProfileSectionType) {
      if (!this.selectedContentFilterProfileData[section]) {
        return [];
      }
      const text = this.selectedContentFilterProfileData[section].text.map(
        (item: ContentFilterEntryMatch) => {
          item.type = 'text';
          return item;
        }
      );
      const regex = this.selectedContentFilterProfileData[section].regex.map(
        (item: ContentFilterEntryMatch) => {
          item.type = 'regex';
          return item;
        }
      );
      return _.concat(text, regex).map((item) => {
        if (!item.id) {
          item.id = DatasetsUtils.generateUUID2();
        }
        return item;
      });
    },

    updateEntryIgnoreContentFilterRuleTags(
      entry: ContentFilterEntryMatch,
      ignoreContentFilterRuleTags: string[]
    ) {
      entry.ignore_cf_rule_tags =
        ignoreContentFilterRuleTags.length > 0 ? ignoreContentFilterRuleTags : [];
      entry.ignore_cf_rule_tags = _.filter(entry.ignore_cf_rule_tags, (entry) => {
        return entry.trim().length > 0;
      });
    },

    addConstraint(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      this.selectedContentFilterProfileData[section][type].unshift(newEntry);
      this.cancelAddConstraint();
    },

    editConstraint(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      const oldEntryIndex = _.findIndex(
        this.selectedContentFilterProfileData[section][type],
        (entry) => {
          return entry.id === newEntry.id;
        }
      );
      this.selectedContentFilterProfileData[section][type][oldEntryIndex] = newEntry;
      this.cancelEditConstraint(newEntry.id);
    },

    deleteConstraint(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      const index = _.findIndex(this.selectedContentFilterProfileData[section][type], (entry) => {
        return entry.id === newEntry.id;
      });
      this.selectedContentFilterProfileData[section][type].splice(index, 1);
    },

    addExclusion(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      if (!this.selectedContentFilterProfileData[section]) {
        this.selectedContentFilterProfileData[section] = {} as ContentFilterProfileSection;
      }
      if (!this.selectedContentFilterProfileData[section][type]) {
        this.selectedContentFilterProfileData[section][type] = [];
      }
      delete newEntry.key;
      delete newEntry.reg;
      delete newEntry.restrict;
      delete newEntry.mask;
      this.selectedContentFilterProfileData[section][type].unshift(newEntry);
    },

    editExclusion(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      const oldEntryIndex = _.findIndex(
        this.selectedContentFilterProfileData[section][type],
        (entry) => {
          return entry.id === newEntry.id;
        }
      );
      this.selectedContentFilterProfileData[section][type][oldEntryIndex] = newEntry;
    },

    deleteExclusion(section: ContentFilterProfileSectionType, newEntry: ContentFilterEntryMatch) {
      const type: NamesRegexTextType = newEntry.type;
      const index = _.findIndex(this.selectedContentFilterProfileData[section][type], (entry) => {
        return entry.id === newEntry.id;
      });
      this.selectedContentFilterProfileData[section][type].splice(index, 1);
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedContentFilterProfileData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData as ContentFilterProfile, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },

    newConstraintKeyError(
      section: ContentFilterProfileSectionType,
      newEntry: ContentFilterEntryMatch
    ): string {
      const key = (newEntry.key ?? '').trim();
      if (!key) {
        return this.$t('contentFilterProfile.configPage.constraints.emptyKey');
      }
      const duplicateKeyError = this.newConstraintDuplicateKeyError(section, newEntry);
      if (duplicateKeyError) return duplicateKeyError;
      return '';
    },

    newConstraintValueError(newEntry: ContentFilterEntryMatch): string {
      const value = (newEntry.reg ?? '').trim();
      if (!value) {
        return this.$t('contentFilterProfile.configPage.constraints.emptyValue');
      }
      return '';
    },

    newConstraintDuplicateKeyError(
      section: ContentFilterProfileSectionType,
      newEntry: ContentFilterEntryMatch
    ): string {
      // get all entries except the current one
      const allEntries = [
        ...this.selectedContentFilterProfileData[section].names.filter(
          (n) => n.id !== newEntry?.id
        ),
        ...this.selectedContentFilterProfileData[section].regex.filter(
          (r) => r.id !== newEntry?.id
        ),
      ];
      // allsections and headers should be validated case insensitive
      const allKeys = ['allsections', 'headers'].includes(section)
        ? [
            newEntry.key.trim().toLowerCase(),
            ...allEntries.map((entry) => (entry.key ?? '').trim().toLowerCase()),
          ]
        : [newEntry.key.trim(), ...allEntries.map((entry) => (entry.key ?? '').trim())];

      if (new Set(allKeys).size !== allKeys.length) {
        return this.$t('contentFilterProfile.configPage.constraints.duplicateKey');
      }
      return '';
    },

    newConstraintError(
      section: ContentFilterProfileSectionType,
      newEntry: ContentFilterEntryMatch
    ): string {
      if (this.newConstraintKeyError(section, newEntry)) {
        return this.newConstraintKeyError(section, newEntry);
      }
      if (this.newConstraintValueError(newEntry)) {
        return this.newConstraintValueError(newEntry);
      }
      return '';
    },

    cancelAddConstraint() {
      this.$refs.constraintsTable.setShowNewRow(false);
    },

    cancelEditConstraint(id: string) {
      delete this.$refs.constraintsTable.rowsInEditMode[id];
    },
  },
  created() {
    const tagsStore = useTagsStore();

    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'content-filter-profiles') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadActions();
        await this.loadContentFilterProfile(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Content Filter Profile', 'Content Filter Profiles Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          const dropdownDataPromise = this.loadDropdownData();
          const actionsPromise = this.loadActions();
          await Promise.all([dropdownDataPromise, actionsPromise]);
          if (this.isNew) {
            this.selectedContentFilterProfileData = this.newContentFilterProfile();
            this.selectedContentFilterProfileData.name = Utils.generateUniqueEntityName(
              this.selectedContentFilterProfileData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalContentFilterProfileData = _.cloneDeep(
              this.selectedContentFilterProfileData
            );
          } else {
            const contentFilterProfileId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === contentFilterProfileId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadContentFilterProfile(contentFilterProfileId);
              const sections: ContentFilterProfileSectionType[] = [
                'args',
                'cookies',
                'headers',
                'allsections',
                'url',
              ];
              _.forEach(sections, (section) => {
                if (!this.selectedContentFilterProfileData[section]) {
                  this.normalizeSections(section);
                }
              });
              if (!this.selectedContentFilterProfileData['decoding']) {
                this.normalizeDecoding();
              }
              if (!this.selectedContentFilterProfileData['content_type']) {
                this.normalizeContentType();
              }
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedContentFilterProfileData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style scoped lang="scss">
.content-type-options-wrapper,
.decoding-options-wrapper {
  > * {
    margin-bottom: 8px;
  }
}

.graphql-path-input {
  margin-top: 8px;
}

.constraints-card,
.exclusions-card {
  padding: 20px;
}

.new-entry-restrict-checkbox,
.new-entry-mask-checkbox,
.new-entry-case-insensitive-checkbox,
.new-entry-active-checkbox {
  margin-top: 8px;
}

.parameter-field-wrapper {
  display: flex;
  gap: 24px;

  .input-holder {
    width: 100%;
  }
}

.input-holder {
  &.has-icons-left {
    :deep(.input-icon) {
      top: 8px;
      bottom: auto;
    }
  }
}

.domain-header-wrapper {
  display: flex;

  .has-tooltip {
    align-self: center;
    margin-bottom: 1px;
  }
}

:deep(.key-cell),
:deep(.parameter-cell) {
  align-items: center;
  display: flex;
  gap: 4px;
}

:deep(.excluded-tags-cell) {
  align-items: center;
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  padding: 0 2px;
}
</style>
